import { useEffect, useMemo, useRef, useState } from "react";
import { Crown, BadgeCheck, MapPin, Users } from "lucide-react";
import api from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABEL = { lawyer: "Abogado/a", mediator: "Mediador/a", admin: "Admin" };

export default function PioneersCarousel({ title = "Nuestros Embajadores", subtitle, compact = false }) {
  const [items, setItems] = useState(null);
  const trackRef = useRef(null);

  useEffect(() => {
    api.get("/pioneers/showcase").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, []);

  // Auto-scroll loop (pauses on hover, only if enough items to scroll)
  useEffect(() => {
    const el = trackRef.current;
    if (!el || !items || items.length < 4) return;
    let raf;
    let paused = false;
    const speed = 0.45; // px per frame
    const onEnter = () => { paused = true; };
    const onLeave = () => { paused = false; };
    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mouseleave", onLeave);
    const step = () => {
      if (!paused && el) {
        el.scrollLeft += speed;
        if (el.scrollLeft >= el.scrollWidth / 2) {
          el.scrollLeft = 0;
        }
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("mouseenter", onEnter);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, [items]);

  if (items === null) {
    return (
      <section className={compact ? "py-10" : "py-16"} data-testid="pioneers-carousel-loading">
        <div className="container-app text-center text-sm text-muted-foreground">Cargando embajadores…</div>
      </section>
    );
  }

  if (items.length === 0) {
    return (
      <section className={compact ? "py-10" : "py-16"} data-testid="pioneers-carousel-empty">
        <div className="container-app">
          {!compact && (
            <div className="text-center max-w-2xl mx-auto mb-10">
              <div className="label-eyebrow">{title}</div>
              <h2 className="serif text-4xl md:text-5xl mt-3 leading-tight">
                Sé el <span className="text-amber-300">primero</span> en esta página.
              </h2>
              <p className="mt-4 text-muted-foreground">
                Los próximos 50 colegas que se sumen tendrán su fotografía destacada acá para siempre.
              </p>
            </div>
          )}
          <EmptyTease />
        </div>
      </section>
    );
  }

  // Duplicate items for seamless loop only when enough to scroll
  const looped = items.length >= 4 ? [...items, ...items] : items;
  const showFillers = items.length < 5;
  const fillersNeeded = Math.max(0, 5 - items.length);

  return (
    <section className={compact ? "py-10" : "py-16"} data-testid="pioneers-carousel">
      <div className="container-app">
        {!compact && (
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="label-eyebrow">{title}</div>
            <h2 className="serif text-4xl md:text-5xl mt-3 leading-tight">
              Ya somos <span className="text-amber-300">{items.length}</span> colegas construyendo Colega Link.
            </h2>
            {subtitle && <p className="mt-4 text-muted-foreground">{subtitle}</p>}
          </div>
        )}

        <div
          ref={trackRef}
          className={`flex gap-4 overflow-x-auto scrollbar-hide pb-4 ${items.length >= 4 ? "snap-x snap-mandatory" : "justify-center flex-wrap"}`}
          style={{ scrollBehavior: "auto" }}
          data-testid="pioneers-carousel-track"
        >
          {looped.map((p, i) => (
            <PioneerCard key={`${p.id}-${i}`} p={p} />
          ))}
          {showFillers && Array.from({ length: fillersNeeded }).map((_, i) => (
            <GhostCard key={`ghost-${i}`} number={items.length + i + 1} />
          ))}
        </div>

        {!compact && (
          <div className="mt-6 text-center text-xs text-muted-foreground flex items-center justify-center gap-2">
            <Users size={12} /> Foto + nombre publicados solo con su consentimiento expreso · sin datos de contacto.
          </div>
        )}
      </div>
    </section>
  );
}

function PioneerCard({ p }) {
  const initials = useMemo(() => (p.display_name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase(), [p.display_name]);
  const location = useMemo(() => [p.comuna, p.region].filter(Boolean).join(", "), [p.comuna, p.region]);
  const accent = p.is_founder ? "amber" : "cyan";
  return (
    <div
      className={`shrink-0 w-[260px] panel p-5 snap-start relative overflow-hidden ${p.is_founder ? "ring-2 ring-amber-400/40" : "ring-1 ring-white/5"}`}
      data-testid={`pioneer-card-${p.id}`}
    >
      <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-20 blur-2xl"
           style={{ background: accent === "amber" ? "hsl(45 95% 55%)" : "hsl(192 95% 52%)" }}/>
      {/* Avatar */}
      <div className="relative w-20 h-20 rounded-full mx-auto overflow-hidden ring-2"
           style={{ borderColor: accent === "amber" ? "hsl(45 95% 55% / 0.5)" : "hsl(192 95% 52% / 0.4)",
                    boxShadow: accent === "amber" ? "0 0 24px hsl(45 95% 55% / 0.25)" : undefined,
                    "--tw-ring-color": accent === "amber" ? "hsl(45 95% 55% / 0.5)" : "hsl(192 95% 52% / 0.4)" }}>
        {p.avatar_url ? (
          <img
            src={`${BACKEND}${p.avatar_url}`}
            alt={p.display_name}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-white/5 text-foreground/70 serif text-2xl">
            {initials}
          </div>
        )}
      </div>

      {/* Badge */}
      <div className="mt-3 flex justify-center">
        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wider ${
          p.is_founder ? "bg-amber-400/15 text-amber-300 border border-amber-400/40" : "bg-cyan-400/15 text-cyan-300 border border-cyan-400/30"
        }`}>
          <Crown size={11} />
          {p.is_founder ? `FUNDADOR N°${String(p.number || 0).padStart(2, "0")}` : `PIONERO N°${String(p.number || 0).padStart(2, "0")}`}
        </span>
      </div>

      {/* Name */}
      <div className="mt-3 text-center">
        <div className="serif text-lg leading-tight">{p.display_name}</div>
        <div className="text-[11px] text-muted-foreground mt-0.5">{ROLE_LABEL[p.role] || p.role}</div>
      </div>

      {/* Location */}
      {location && (
        <div className="mt-2 flex items-center justify-center gap-1 text-[11px] text-muted-foreground">
          <MapPin size={10} />
          <span>{location}</span>
        </div>
      )}

      {/* Specialties */}
      {p.specialties && p.specialties.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1 justify-center">
          {p.specialties.map((s) => (
            <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-muted-foreground">{s}</span>
          ))}
        </div>
      )}

      {p.verified && (
        <div className="mt-3 flex items-center justify-center gap-1 text-[10px] text-[hsl(var(--accent))]">
          <BadgeCheck size={11} /> Verificado
        </div>
      )}
    </div>
  );
}

function EmptyTease() {
  // Render 5 ghost cards as placeholder to give a sense of "your face here"
  return (
    <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-4 justify-center flex-wrap">
      {Array.from({ length: 5 }).map((_, i) => (
        <GhostCard key={i} number={i + 1} />
      ))}
    </div>
  );
}

function GhostCard({ number = 1 }) {
  return (
    <div className="shrink-0 w-[260px] panel p-5 ring-1 ring-amber-400/20 border border-dashed border-amber-400/20 bg-amber-400/[0.02]" data-testid={`pioneer-ghost-${number}`}>
      <div className="w-20 h-20 rounded-full mx-auto bg-amber-400/10 border-2 border-dashed border-amber-400/30 flex items-center justify-center">
        <Crown size={20} className="text-amber-400/40" />
      </div>
      <div className="mt-3 text-center">
        <div className="text-[10px] tracking-wider text-amber-400/60 font-bold">PIONERO N°{String(number).padStart(2, "0")}</div>
        <div className="serif text-base text-foreground/40 mt-1">¿Tu nombre acá?</div>
        <div className="text-[10px] text-muted-foreground mt-1">Cupo disponible</div>
      </div>
    </div>
  );
}
